import { useRef, useState } from "react";
import { ROOM_SUGGESTIONS } from "../naming/roomNames";

interface Props {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
}

// Namensfeld mit eigener Raumliste: Beim Anklicken erscheinen immer ALLE Räume (auch wenn
// schon ein Name – z. B. ein KI-Vorschlag – drinsteht). Erst beim Tippen wird gefiltert.
export function RoomNameInput({ value, placeholder, onChange, disabled, label }: Props) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const q = filter.trim().toLowerCase();
  const options = q ? ROOM_SUGGESTIONS.filter((r) => r.toLowerCase().includes(q)) : ROOM_SUGGESTIONS;

  const show = () => {
    setFilter("");
    setActive(-1);
    setOpen(true);
  };
  const pick = (room: string) => {
    onChange(room);
    setOpen(false);
  };

  return (
    <div className="relative mt-1.5" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        draggable={false}
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onFocus={(e) => {
          e.currentTarget.select(); // Tippen ersetzt direkt den Vorschlag
          show();
        }}
        onBlur={() => setOpen(false)}
        onChange={(e) => {
          onChange(e.target.value);
          setFilter(e.target.value);
          setActive(-1);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(options.length - 1, i + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          } else if (e.key === "Enter" && open && active >= 0 && options[active]) {
            e.preventDefault();
            pick(options[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        className="w-full pl-2 pr-6 py-1 text-xs rounded border border-kampmeyer-neutral-300 dark:border-kampmeyer-neutral-700 bg-white dark:bg-kampmeyer-neutral-800 text-kampmeyer-neutral-900 dark:text-kampmeyer-neutral-100 placeholder:text-kampmeyer-neutral-500 focus:outline-none focus:ring-2 focus:ring-kampmeyer-primary/40"
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-label="Raumliste öffnen"
        onMouseDown={(e) => {
          e.preventDefault(); // Fokus im Feld lassen
          if (open) setOpen(false);
          else {
            inputRef.current?.focus();
            show();
          }
        }}
        className="absolute right-0 top-0 bottom-0 w-6 flex items-center justify-center text-kampmeyer-neutral-500 hover:text-kampmeyer-neutral-900"
      >
        <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor" aria-hidden="true">
          <path d="M2 4l4 4 4-4z" />
        </svg>
      </button>
      {open && options.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-30 left-0 mt-1 min-w-[180px] w-full max-h-56 overflow-auto rounded-md border border-kampmeyer-neutral-300 bg-white shadow-lg text-xs py-1"
        >
          {options.map((room, i) => (
            <li
              key={room}
              role="option"
              aria-selected={room === value}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(room);
              }}
              onMouseEnter={() => setActive(i)}
              className={`px-2.5 py-1.5 cursor-pointer ${
                i === active ? "bg-kampmeyer-neutral-100" : ""
              } ${room === value ? "font-semibold" : ""}`}
            >
              {room}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
